/**
 * =========================================================================
 * helpers.js (Hybrid Lookup Strategy)
 * =========================================================================
 *
 * This file now implements a "progressive enhancement" strategy for
 * fetching geographic coordinates from a CEP.
 *
 */

import { doc, getDoc, getDocs, collection, query, where, orderBy, limit } from "firebase/firestore";

// --- ACTION REQUIRED ---
// Import the 'db' instance from your central Firebase configuration file.
// Adjust the path ('../firebase') to match your project structure.
import { db } from "../firebase";

// Gets or creates a unique device ID stored in the browser's local storage.
// crypto.randomUUID needs a secure context and a recent browser (Safari 15.4+,
// Chrome 92+). Older phones lack it, which used to make signup fail before the
// profile request was even sent. getRandomValues is available everywhere.
function uuidV4() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID(); } catch (e) { /* fall through */ }
  }
  const b = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function getOrCreateDeviceId() {
  let deviceId = null;
  try { deviceId = localStorage.getItem("trocaTudoDeviceId"); } catch (e) { /* blocked storage */ }
  if (!deviceId) {
    deviceId = uuidV4();
    try { localStorage.setItem("trocaTudoDeviceId", deviceId); } catch (e) { /* ignore */ }
  }
  return deviceId;
}

// Fetches the user's public IP address from a free, reliable service.
export async function getIpAddress() {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Could not fetch IP address:", error);
    return null;
  }
}

/**
 * =========================================================================
 * getGeoFromCEP (High-Accuracy with Fallback)
 * =========================================================================
 *
 * This function first attempts to find a CEP in the high-accuracy
 * 'brazilian_cep_granularity' collection.
 *
 * If a document is not found (because the background build process is not
 * yet complete for that area), it gracefully falls back to querying the
 * less-accurate 'cep_ranges' collection.
 *
 */
export const getGeoFromCEP = async (cep) => {
  const cleanCepString = String(cep).replace(/\D/g, "");

  if (cleanCepString.length !== 8) {
    console.log(`Invalid CEP format: ${cleanCepString}`);
    return null;
  }

  try {
    // --- Step 1: Attempt to get high-accuracy data first ---
    const granularDocRef = doc(db, "brazilian_cep_granularity", cleanCepString);
    const granularDocSnap = await getDoc(granularDocRef);

    if (granularDocSnap.exists()) {
      console.log(`SUCCESS: Found high-accuracy data for CEP ${cleanCepString}.`);
      const data = granularDocSnap.data();
      return {
        lat: data.lat,
        lon: data.lon,
        uf: data.state,
        city: data.city,
        geohash: data.geohash,
      };
    }

    // --- Step 2: If not found, fall back to the cep_ranges collection ---
    console.log(`No granular data for ${cleanCepString}. Falling back to cep_ranges.`);
    const numericCep = parseInt(cleanCepString, 10);
    const rangesRef = collection(db, "cep_ranges");

    const q = query(
      rangesRef,
      where("cep_start", "<=", numericCep),
      orderBy("cep_start", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(`No range found for CEP ${numericCep}.`);
      return null;
    }

    const rangeData = querySnapshot.docs[0].data();

    if (numericCep <= rangeData.cep_end) {
      console.log(`SUCCESS (Fallback): Found range data for CEP ${cleanCepString}.`);
      return {
        lat: rangeData.lat,
        lon: rangeData.lon,
        uf: rangeData.state,
        city: rangeData.city,
        geohash: rangeData.geohash,
      };
    } else {
      console.log(`Fallback failed for CEP ${cleanCepString}.`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching CEP data for ${cleanCepString}:`, error);
    return null;
  }
};

// Calculates the Haversine distance between two lat/lon points in kilometers.
export function haversineDistance(coords1, coords2) {
  function toRad(x) {
    return x * Math.PI / 180;
  }

  const lon1 = coords1.lon;
  const lat1 = coords1.lat;
  const lon2 = coords2.lon;
  const lat2 = coords2.lat;

  const R = 6371; // Earth's radius in km

  const x1 = lat2 - lat1;
  const dLat = toRad(x1);
  const x2 = lon2 - lon1;
  const dLon = toRad(x2);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;

  return d;
}

